import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Button, Spin, Alert } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined, CheckOutlined, CloudOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { avecLibelleTraduit } from '../../../i18n/libelles';
import axiosInstance from '../../../variables/api/axiosInstance';
import Layout from '../../../components/Layout';
import { T } from './declarationTheme';
import StepperSidebar from './StepperSidebar';
import StepContexte from './StepContexte';
import StepConducteurContrat from './StepConducteurContrat';
import StepAccident from './StepAccident';
import StepPartiesAdverses from './StepPartiesAdverses';
import StepVictimes from './StepVictimes';
import StepRecapitulatif from './StepRecapitulatif';
import useDraftSinistre from './useDraftSinistre';
import { joindrePiecesHomologue } from '../../../services/ficheHomologueService';
import { assureurEmetteur } from './assureurEmetteur';
import CBModal from '../../../components/CBModal';
import { isDossierMurOuPlus, splitCategoriesPermis } from '../../../variables/constants';

/* `label` et `hint` sont traduits à la lecture (declaration:etapes). */

const STEPS = avecLibelleTraduit(avecLibelleTraduit([
  { key: 'contexte' },
  { key: 'cond_ctrt' },
  { key: 'accident' },
  { key: 'adverses' },
  { key: 'victimes' },
  { key: 'recap' },
], 'declaration:etapes.libelles', { cle: 'key' }), 'declaration:etapes.indications', { cle: 'key', champ: 'hint' });

/* Champs requis par étape */
const REQUIRED_FIELDS = {
  0: ['typeSinistre'],
  1: ['numeroPolice', 'numeroAttestation', 'organismeMembreTrackingId', 'assureNom', 'immatriculation'],
  2: ['dateAccident', 'dateDeclaration', 'lieuAccident', 'typeDommage'],
  3: [], // parties adverses : tout est validé dans la modale
  4: ['motifModificationVictimes'], // validé seulement quand le champ est monté (bilan modifié)
  5: ['declarantNom', 'declarantQualite'],
};

export default function DeclarationSinistre() {
  const { t } = useTranslation('declaration');
  const navigate = useNavigate();
  const { id: editingId } = useParams();
  const isEdit = Boolean(editingId);
  const [form] = Form.useForm();

  /* Référentiels */
  const [pays, setPays] = useState([]);
  const [organismes, setOrganismes] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  /* État wizard */
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState(isEdit ? [0, 1, 2, 3, 4, 5] : []);
  const [victimes, setVictimes] = useState([]);
  const [adversaires, setAdversaires] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  /* TE : fiche de déclaration du bureau homologue (obligatoire) et autres pièces.
     Hors brouillon : un fichier ne se sérialise pas. */
  const [piecesHomologue, setPiecesHomologue] = useState({ fiche: null, autres: [] });
  const [confirmQuitOpen, setConfirmQuitOpen] = useState(false);
  const [editBlocked, setEditBlocked] = useState(null);
  /* Snapshot des IDs victimes/adversaires présents au serveur au chargement (pour détecter les suppressions) */
  const [initialVictimeIds, setInitialVictimeIds] = useState([]);
  /* Bilan humain annoncé au chargement (mode édition) : sert à exiger un motif
     quand les nombres sont modifiés - même contrôle que le backend. */
  const [bilanInitial, setBilanInitial] = useState(null);

  const { initial, status: draftStatus, persist, clear } = useDraftSinistre();

  /* Chargement référentiels + restauration brouillon ou pré-remplissage édition */
  useEffect(() => {
    Promise.all([
      axiosInstance.get('/v1/pays').then(r => r.data?.data || []).catch(() => []),
      axiosInstance.get('/v1/organismes?size=200').then(r => r.data?.data?.content || r.data?.data || []).catch(() => []),
    ]).then(([p, o]) => {
      setPays(p); setOrganismes(o);
    }).finally(async () => {
      if (isEdit) {
        try {
          const sRes = await axiosInstance.get(`/v1/sinistres/${editingId}`);
          const s = sRes.data?.data;
          if (!s) throw new Error(i18n.t('declaration:declarationSinistre.messages.sinistreIntrouvable'));

          if (isDossierMurOuPlus(s.statut)) {
            setEditBlocked(
              i18n.t('declaration:declarationSinistre.messages.dossierMur', { statut: s.statut })
            );
            setLoadingRefs(false);
            return;
          }

          let a = null;
          if (s.assureTrackingId) {
            try {
              const aRes = await axiosInstance.get(`/v1/assures/${s.assureTrackingId}`);
              a = aRes.data?.data || null;
            } catch { /* ignore */ }
          }

          try {
            const vRes = await axiosInstance.get(`/v1/victimes/sinistre/${editingId}`);
            const all = vRes.data?.data || [];
            setVictimes(all.filter(x => !x.estAdversaire));
            setAdversaires(all.filter(x => x.estAdversaire));
            setInitialVictimeIds(all.map(x => x.victimeTrackingId).filter(Boolean));
          } catch { /* ignore */ }

          form.setFieldsValue(buildFormValuesFromSinistre(s, a));
          setBilanInitial({
            total: s.nombreVictimesAttendu ?? null,
            blesses: s.nombreBlessesAttendu ?? null,
            decedes: s.nombreDecedesAttendu ?? null,
          });
        } catch (err) {
          setEditBlocked(err?.response?.data?.message || err?.message || i18n.t('declaration:declarationSinistre.messages.chargementImpossible'));
        }
      } else if (initial) {
        const { _savedAt, _victimes, _adversaires, _step, _completed, ...rest } = initial;
        const restored = {
          ...rest,
          dateAccident: rest.dateAccident ? dayjs(rest.dateAccident) : null,
          dateDeclaration: rest.dateDeclaration ? dayjs(rest.dateDeclaration) : dayjs(),
          heureAccident: rest.heureAccident ? dayjs(rest.heureAccident) : null,
          dateEffet: rest.dateEffet ? dayjs(rest.dateEffet) : null,
          dateEcheance: rest.dateEcheance ? dayjs(rest.dateEcheance) : null,
          prochaineVT: rest.prochaineVT ? dayjs(rest.prochaineVT) : null,
          conducteurDateNaissance: rest.conducteurDateNaissance ? dayjs(rest.conducteurDateNaissance) : null,
          conducteurDateDelivrance: rest.conducteurDateDelivrance ? dayjs(rest.conducteurDateDelivrance) : null,
        };
        form.setFieldsValue(restored);
        if (Array.isArray(_victimes)) setVictimes(_victimes);
        if (Array.isArray(_adversaires)) setAdversaires(_adversaires);
        if (typeof _step === 'number') setStep(_step);
        if (Array.isArray(_completed)) setCompleted(_completed);
      } else {
        form.setFieldsValue({
          dateDeclaration: dayjs(),
          conducteurEstAssure: true,
        });
      }
      setLoadingRefs(false);
    });
  }, [form, initial, isEdit, editingId]);

  /* Auto-save (désactivé en mode édition) */
  const values = Form.useWatch([], form) || {};
  useEffect(() => {
    if (loadingRefs || isEdit) return;
    const snapshot = {
      ...values,
      dateAccident: values.dateAccident?.toISOString?.() || values.dateAccident || null,
      dateDeclaration: values.dateDeclaration?.toISOString?.() || values.dateDeclaration || null,
      heureAccident: values.heureAccident?.toISOString?.() || values.heureAccident || null,
      dateEffet: values.dateEffet?.toISOString?.() || values.dateEffet || null,
      dateEcheance: values.dateEcheance?.toISOString?.() || values.dateEcheance || null,
      prochaineVT: values.prochaineVT?.toISOString?.() || values.prochaineVT || null,
      conducteurDateNaissance: values.conducteurDateNaissance?.toISOString?.() || values.conducteurDateNaissance || null,
      conducteurDateDelivrance: values.conducteurDateDelivrance?.toISOString?.() || values.conducteurDateDelivrance || null,
      _victimes: victimes,
      _adversaires: adversaires,
      _step: step,
      _completed: completed,
    };
    persist(snapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, victimes, adversaires, step, completed, loadingRefs, isEdit]);

  /* Validation d'une étape */
  const validateStep = useCallback(async (idx) => {
    const fields = REQUIRED_FIELDS[idx] || [];
    if (fields.length === 0) return true;
    try {
      await form.validateFields(fields);
      return true;
    } catch {
      return false;
    }
  }, [form]);

  const goNext = useCallback(async () => {
    const ok = await validateStep(step);
    if (!ok) { toast.error(t('declarationSinistre.messages.champsEnErreur')); return; }
    setCompleted((prev) => prev.includes(step) ? prev : [...prev, step]);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, validateStep, t]);

  const goPrev = useCallback(() => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToStep = useCallback(async (idx) => {
    if (idx === step) return;
    if (idx < step || completed.includes(idx - 1) || idx === 0) {
      setStep(idx);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const ok = await validateStep(step);
      if (ok) {
        setCompleted((prev) => prev.includes(step) ? prev : [...prev, step]);
        setStep(idx);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [step, completed, validateStep]);

  /* ─── Soumission (3 appels chainés + victimes + adversaires) ─── */
  const submit = useCallback(async () => {
    const allFields = Object.values(REQUIRED_FIELDS).flat();
    try { await form.validateFields(allFields); }
    catch { toast.error(t('declarationSinistre.messages.champsManquants')); return; }

    if (!isEdit && form.getFieldValue('typeSinistre') === 'SURVENU_ETRANGER' && !piecesHomologue.fiche) {
      toast.error(t('declarationSinistre.messages.ficheHomologueManquante'));
      return;
    }

    setSubmitting(true);
    try {
      const v = form.getFieldsValue(true);

      /* ─── Mode édition : PUT de l'assuré puis du sinistre ─── */
      if (isEdit) {
        // 1. Mise à jour de l'assuré si on a son trackingId
        const assureTrackingId = v.assureTrackingIdExistant;
        if (assureTrackingId) await mettreAJourAssure(assureTrackingId, v);

        // 2. Mise à jour du sinistre
        // Le backend versionne le sinistre : à chaque update, un nouveau trackingId
        // est généré. On l'utilise pour la suite (victimes, navigation).
        // Cas "aucune modif sur le sinistre" (422) : on continue silencieusement
        // si l'utilisateur n'édite que les victimes/adversaires.
        const sinistrePayload = buildSinistrePayload(v, organismes);
        let updatedSinistreId = editingId;
        try {
          const sinistreRes = await axiosInstance.put(
            `/v1/sinistres/${editingId}`,
            sinistrePayload,
            { skipErrorToast: true },
          );
          updatedSinistreId = sinistreRes.data?.data?.sinistreTrackingId || editingId;
        } catch (e) {
          if (e?.response?.status !== 422) throw e;
        }

        // 3. Synchronisation des victimes/adversaires
        const echecs = [];
        const currentIds = [...victimes, ...adversaires]
          .map(x => x.victimeTrackingId)
          .filter(Boolean);

        // 3a. Supprimer les victimes/adversaires retirés (présents au serveur, absents du state)
        const toDelete = initialVictimeIds.filter(id => !currentIds.includes(id));
        for (const idDel of toDelete) {
          try { await axiosInstance.delete(`/v1/victimes/${idDel}`); }
          catch { echecs.push(t('declarationSinistre.messages.suppressionVictime', { id: idDel.slice(0, 8) })); }
        }

        // 3b. Victimes réelles : PUT si existante, POST si nouvelle
        for (const vi of victimes) {
          const bodyVictime = {
            nom: vi.nom,
            prenoms: vi.prenoms,
            dateNaissance: vi.dateNaissance,
            sexe: vi.sexe,
            nationalite: vi.nationalite || null,
            email: vi.email || null,
            telephone: vi.telephone || null,
            langue:                  vi.langue || null,
            statutActivite: vi.statutActivite,
            revenuMensuel: vi.revenuMensuel ?? 0,
            paysResidenceTrackingId: vi.paysResidenceTrackingId || null,
            sinistreTrackingId: updatedSinistreId,
            typeVictime: vi.typeVictime,
            typeDommage: vi.typeDommage || null,
            estAdversaire: false,
            profession: vi.profession || null,
            tauxRc: vi.tauxRc != null ? Number(vi.tauxRc) : null,
          };
          try {
            if (vi.victimeTrackingId) {
              await axiosInstance.put(`/v1/victimes/${vi.victimeTrackingId}`, bodyVictime);
            } else {
              await axiosInstance.post('/v1/victimes', bodyVictime);
            }
          } catch { echecs.push(`${vi.nom} ${vi.prenoms}`); }
        }

        // 3c. Adversaires : PUT si existant, POST si nouveau
        for (const a of adversaires) {
          const bodyAdv = {
            nom: a.nom || 'ADVERSAIRE',
            prenoms: a.prenoms || '-',
            // Plus de valeurs inventées (né aujourd'hui, masculin, actif) : une partie
            // adverse blessée devient une victime, et ces valeurs fausseraient son indemnité.
            dateNaissance: a.dateNaissance || null,
            sexe: a.sexe || null,
            nationalite: a.nationalite || null,
            statutActivite: a.statutActivite || null,
            revenuMensuel: a.revenuMensuel ?? 0,
            profession: a.profession || null,
            paysResidenceTrackingId: a.paysResidenceTrackingId || null,
            sinistreTrackingId: updatedSinistreId,
            estAdversaire: true,
            typeAdversaire: a.typeAdversaire || 'ENGINS',
            typeAdversaireAutre: a.typeAdversaireAutre || null,
            typeVictime: a.typeVictime || 'NEUTRE',
            telephone: a.telephone || null,
            email: a.email || null,
            numeroPermis: a.numeroPermis || null,
            categoriesPermis: splitCategoriesPermis(a.categoriesPermis),
            dateDelivrance: a.dateDelivrance || null,
            lieuDelivrance: a.lieuDelivrance || null,
            marqueVehicule: a.marqueVehicule || null,
            modeleVehicule: a.modeleVehicule || null,
            genreVehicule: a.genreVehicule || null,
            couleurVehicule: a.couleurVehicule || null,
            immatriculation: a.immatriculation || null,
            numeroChassis: a.numeroChassis || null,
            capaciteVehicule: a.capaciteVehicule ?? null,
            proprietaireVehicule: a.proprietaireVehicule || null,
            aRemorque: !!a.aRemorque,
            remorqueMarque: a.remorqueMarque || null,
            remorqueModele: a.remorqueModele || null,
            remorqueImmatriculation: a.remorqueImmatriculation || null,
            remorqueAssureur: a.remorqueAssureur || null,
            prochaineVT: a.prochaineVT || null,
            nbPersonnesABord: a.nbPersonnesABord || 0,
            assureurAdverse: a.assureurAdverse || null,
            typeDommage: a.typeDommage || null,
            descriptionDegats: a.descriptionDegats || null,
            blesses: a.blesses || 0,
            deces: a.deces || 0,
            tauxRc: a.tauxRc != null ? Number(a.tauxRc) : null,
          };
          try {
            if (a.victimeTrackingId) {
              await axiosInstance.put(`/v1/victimes/${a.victimeTrackingId}`, bodyAdv);
            } else {
              await axiosInstance.post('/v1/victimes', bodyAdv);
            }
          } catch { echecs.push(t('declarationSinistre.messages.adversaire', { reference: a.immatriculation || a.nom || '' })); }
        }

        if (echecs.length === 0) {
          toast.success(t('declarationSinistre.messages.declarationMiseAJour'));
        } else {
          toast.warning(t('declarationSinistre.messages.declarationMiseAJourErreurs', { erreurs: echecs.join(', ') }));
        }
        navigate(`/sinistres/${updatedSinistreId}`);
        return;
      }

      /* ── 1. Assuré : réutiliser ou créer ──
         Un assuré retrouvé par son n° d'attestation est MIS À JOUR avec ce que
         l'agent a saisi. Il était auparavant réutilisé tel quel : police,
         propriétaire, compagnie corrigés après la recherche étaient perdus sans
         un mot - et la compagnie de l'assuré est celle dont le code entre dans le
         numéro d'un sinistre TE. */
      let assureTrackingId = v.assureTrackingIdExistant || null;
      if (assureTrackingId) {
        await mettreAJourAssure(assureTrackingId, v);
      } else {
        const assureRes = await axiosInstance.post('/v1/assures', buildAssurePayload(v));
        assureTrackingId = assureRes.data?.data?.assureTrackingId;
        if (!assureTrackingId) throw new Error(t('declarationSinistre.messages.echecAssure'));
      }

      /* ── 2. Sinistre ── */
      const sinistrePayload = { ...buildSinistrePayload(v, organismes), assureTrackingId };
      const sinistreRes = await axiosInstance.post('/v1/sinistres', sinistrePayload);
      const sinistre = sinistreRes.data?.data;
      const sinistreTrackingId = sinistre?.sinistreTrackingId;
      if (!sinistreTrackingId) throw new Error(t('declarationSinistre.messages.numeroNonRetourne'));

      /* ── 3. Victimes ── */
      const echecs = [];
      for (const vi of victimes) {
        try {
          await axiosInstance.post('/v1/victimes', {
            nom: vi.nom,
            prenoms: vi.prenoms,
            dateNaissance: vi.dateNaissance,
            sexe: vi.sexe,
            nationalite: vi.nationalite || null,
            email: vi.email || null,
            telephone: vi.telephone || null,
            langue:                  vi.langue || null,
            statutActivite: vi.statutActivite,
            revenuMensuel: vi.revenuMensuel ?? 0,
            paysResidenceTrackingId: vi.paysResidenceTrackingId || null,
            sinistreTrackingId,
            /* Extras */
            typeVictime: vi.typeVictime,
            typeDommage: vi.typeDommage || null,
            estAdversaire: false,
            profession: vi.profession || null,
            tauxRc: vi.tauxRc != null ? Number(vi.tauxRc) : null,
          });
        } catch (e) {
          echecs.push(`${vi.nom} ${vi.prenoms}`);
        }
      }

      /* ── 4. Adversaires ── */
      for (const a of adversaires) {
        try {
          await axiosInstance.post('/v1/victimes', {
            nom: a.nom || 'ADVERSAIRE',
            prenoms: a.prenoms || '-',
            // Plus de valeurs inventées (né aujourd'hui, masculin, actif) : une partie
            // adverse blessée devient une victime, et ces valeurs fausseraient son indemnité.
            dateNaissance: a.dateNaissance || null,
            sexe: a.sexe || null,
            nationalite: a.nationalite || null,
            statutActivite: a.statutActivite || null,
            revenuMensuel: a.revenuMensuel ?? 0,
            profession: a.profession || null,
            paysResidenceTrackingId: a.paysResidenceTrackingId || null,
            sinistreTrackingId,
            /* Extras adversaire */
            estAdversaire: true,
            typeAdversaire: a.typeAdversaire || 'ENGINS',
            typeAdversaireAutre: a.typeAdversaireAutre || null,
            typeVictime: a.typeVictime || 'NEUTRE',
            telephone: a.telephone || null,
            email: a.email || null,
            numeroPermis: a.numeroPermis || null,
            categoriesPermis: splitCategoriesPermis(a.categoriesPermis),
            dateDelivrance: a.dateDelivrance || null,
            lieuDelivrance: a.lieuDelivrance || null,
            marqueVehicule: a.marqueVehicule || null,
            modeleVehicule: a.modeleVehicule || null,
            genreVehicule: a.genreVehicule || null,
            couleurVehicule: a.couleurVehicule || null,
            immatriculation: a.immatriculation || null,
            numeroChassis: a.numeroChassis || null,
            prochaineVT: a.prochaineVT || null,
            capaciteVehicule: a.capaciteVehicule ?? null,
            nbPersonnesABord: a.nbPersonnesABord || 0,
            proprietaireVehicule: a.proprietaireVehicule || null,
            aRemorque: !!a.aRemorque,
            remorqueMarque: a.remorqueMarque || null,
            remorqueModele: a.remorqueModele || null,
            remorqueImmatriculation: a.remorqueImmatriculation || null,
            remorqueAssureur: a.remorqueAssureur || null,
            assureurAdverse: a.assureurAdverse || null,
            typeDommage: a.typeDommage || null,
            descriptionDegats: a.descriptionDegats || null,
            blesses: a.blesses || 0,
            deces: a.deces || 0,
            tauxRc: a.tauxRc != null ? Number(a.tauxRc) : null,
          });
        } catch (e) {
          echecs.push(t('declarationSinistre.messages.adversaire', { reference: a.immatriculation || a.nom || '' }));
        }
      }

      /* ── 5. TE : pièces reçues du bureau homologue ──
         Après les victimes : c'est la GED du dossier créé qui les reçoit. */
      if (v.typeSinistre === 'SURVENU_ETRANGER') {
        const echecsPieces = await joindrePiecesHomologue(sinistre, piecesHomologue, v.dateDeclaration);
        echecs.push(...echecsPieces);
      }

      if (echecs.length === 0) {
        // Le message vient du serveur : c'est lui qui sait si la déclaration entre
        // dans le flux ou reste en instance de validation, en attente d'affectation.
        // Annoncer « déclaré avec succès » quand le dossier part en corbeille
        // laisserait croire qu'il est pris en charge, alors que personne ne l'a.
        toast.success(
          sinistreRes.data?.message
          || (v.typeSinistre === 'SURVENU_ETRANGER' ? t('declarationSinistre.messages.sinistreEnregistre', { numero: sinistre.numeroSinistreLocal || '' }) : t('declarationSinistre.messages.sinistreDeclare', { numero: sinistre.numeroSinistreLocal || '' })),
        );
      } else {
        toast.warning(
          t('declarationSinistre.messages.sinistreCreeAvecErreurs', { numero: sinistre.numeroSinistreLocal || '', erreurs: echecs.join(', ') })
        );
      }
      clear();
      navigate(`/sinistres/${sinistreTrackingId}/fiche?fresh=1`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || t('declarationSinistre.messages.erreurDeclaration'));
    } finally {
      setSubmitting(false);
    }
  }, [form, victimes, adversaires, clear, navigate, isEdit, editingId, initialVictimeIds, piecesHomologue, organismes, t]);

  /* Rendu étape */
  const stepContent = useMemo(() => {
    switch (step) {
      case 0: return <StepContexte form={form} pays={pays} organismes={organismes} />;
      case 1: return <StepConducteurContrat form={form} organismes={organismes} pays={pays} />;
      case 2: return <StepAccident form={form} pays={pays} />;
      case 3: return (
        <StepPartiesAdverses
          adversaires={adversaires} setAdversaires={setAdversaires}
        />
      );
      case 4: return (
        <StepVictimes
          form={form}
          victimes={victimes} setVictimes={setVictimes}
          adversaires={adversaires}
          pays={pays}
          bilanInitial={bilanInitial}
        />
      );
      case 5: return (
        <StepRecapitulatif
          form={form}
          values={form.getFieldsValue(true)}
          victimes={victimes}
          adversaires={adversaires}
          pays={pays}
          organismes={organismes}
          goToStep={setStep}
          piecesHomologue={piecesHomologue}
          setPiecesHomologue={isEdit ? undefined : setPiecesHomologue}
        />
      );
      default: return null;
    }
  }, [step, form, pays, organismes, victimes, adversaires, bilanInitial, piecesHomologue, isEdit]);

  const isLast = step === STEPS.length - 1;
  /* TE : le sinistre est déclaré par le bureau homologue, qui instruit le dossier ;
     le bureau l'enregistre à partir de sa fiche. `preserve` : le type se choisit à
     l'étape Contexte, démontée au moment du récapitulatif (voir DeclarantBlock). */
  const estTe = Form.useWatch('typeSinistre', { form, preserve: true }) === 'SURVENU_ETRANGER';

  return (
    <Layout title={isEdit ? t('declarationSinistre.titreModifier') : t('declarationSinistre.titreNouvelle')}>
      <div
        style={{
          background: T.white,
          border: `1px solid ${T.line}`,
          borderRadius: T.r.lg,
          overflow: 'hidden',
          display: 'flex',
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <StepperSidebar
          steps={STEPS}
          current={step}
          completed={completed}
          onGoto={goToStep}
        />

        <main style={{ flex: 1, padding: '48px 56px 56px', minWidth: 0, position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 36 }}>
            <button
              type="button"
              onClick={() => isEdit ? navigate(`/sinistres/${editingId}`) : setConfirmQuitOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                border: 'none', background: 'none', color: T.inkMuted,
                fontSize: 12.5, cursor: 'pointer', padding: 0,
              }}
            >
              <CloseOutlined style={{ fontSize: 11 }} /> {isEdit ? t('commun.annuler') : t('declarationSinistre.quitter')}
            </button>
            {!isEdit && <DraftIndicator status={draftStatus} />}
          </div>

          {loadingRefs ? (
            <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>
          ) : editBlocked ? (
            <Alert
              type="warning"
              showIcon
              message={t('declarationSinistre.modificationImpossible')}
              description={editBlocked}
              action={
                <Button size="small" onClick={() => navigate(`/sinistres/${editingId}`)}>
                  {t('declarationSinistre.retourDossier')}
                </Button>
              }
            />
          ) : (
            <Form form={form} layout="vertical" requiredMark={false} preserve>
              {stepContent}
            </Form>
          )}

          <nav
            style={{
              marginTop: 48,
              paddingTop: 20,
              borderTop: `1px solid ${T.line}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: 12.5, color: T.inkMuted }}>
              <Trans
                t={t}
                i18nKey="declarationSinistre.etapeSur"
                values={{ numero: step + 1, total: STEPS.length }}
                components={{ 1: <strong style={{ color: T.ink, fontWeight: 600 }} />, 2:
              <span style={{ color: T.inkLight }} /> }}
              />
              <span style={{ marginLeft: 10 }}>· {STEPS[step].label}</span>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {step > 0 && (
                <Button
                  onClick={goPrev}
                  icon={<ArrowLeftOutlined />}
                  style={{ height: 40, padding: '0 20px', borderRadius: T.r.md, borderColor: T.inkGhost, color: T.inkSoft, fontWeight: 500 }}
                >
                  {t('declarationSinistre.precedent')}
                </Button>
              )}
              {!isLast ? (
                <Button
                  onClick={goNext}
                  type="primary"
                  style={{ height: 40, padding: '0 24px', borderRadius: T.r.md, background: T.brand, borderColor: T.brand, fontWeight: 600, minWidth: 140 }}
                >
                  {t('declarationSinistre.continuer')} <ArrowRightOutlined />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  type="primary"
                  loading={submitting}
                  disabled={Boolean(editBlocked)}
                  icon={<CheckOutlined />}
                  style={{ height: 40, padding: '0 28px', borderRadius: T.r.md, background: T.brand, borderColor: T.brand, fontWeight: 600, minWidth: 180 }}
                >
                  {isEdit ? t('declarationSinistre.enregistrerModifications') : estTe ? t('declarationSinistre.enregistrerSinistre') : t('declarationSinistre.declarerSinistre')}
                </Button>
              )}
            </div>
          </nav>
        </main>
      </div>

      <CBModal
        open={confirmQuitOpen}
        onCancel={() => setConfirmQuitOpen(false)}
        title={t('declarationSinistre.quitterModale.titre')}
        okText={t('declarationSinistre.quitterModale.ok')}
        cancelText={t('declarationSinistre.quitterModale.annuler')}
        okButtonProps={{ style: { background: T.brand, borderColor: T.brand } }}
        onOk={() => navigate('/sinistres')}
      >
        <p style={{ color: T.inkSoft, marginBottom: 8 }}>
          {t('declarationSinistre.quitterModale.sauvegardeAuto')}
        </p>
        <p style={{ color: T.inkMuted, fontSize: 13 }}>
          {t('declarationSinistre.quitterModale.reprendre')}
        </p>
        <div style={{
          marginTop: 16,
          padding: '12px 14px',
          background: '#fef3f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>
              {t('declarationSinistre.quitterModale.toutEffacer')}
            </div>
            <div style={{ fontSize: 11.5, color: '#7f1d1d', marginTop: 2 }}>
              {t('declarationSinistre.quitterModale.toutEffacerTexte')}
            </div>
          </div>
          <Button
            danger
            type="primary"
            icon={<CloseOutlined />}
            onClick={() => {
              clear(); form.resetFields();
              setVictimes([]); setAdversaires([]); setStep(0); setCompleted([]);
              setConfirmQuitOpen(false); toast.info(t('declarationSinistre.messages.brouillonEfface'));
            }}
          >
            {t('declarationSinistre.quitterModale.reinitialiser')}
          </Button>
        </div>
      </CBModal>
    </Layout>
  );
}

/* Construit le payload assuré commun (POST création + PUT mise à jour). */
function buildAssurePayload(v) {
  const nomAssure = v.assureNom?.trim();
  const prenomAssure = v.assurePrenom?.trim() || null;
  return {
    nomAssure,
    prenomAssure,
    nomComplet: [nomAssure, prenomAssure].filter(Boolean).join(' ').trim(),
    numeroPolice: v.numeroPolice?.trim() || null,
    numeroAttestation: v.numeroAttestation?.trim(),
    numeroCGrise: v.numeroCGrise?.trim() || null,
    proprietaireVehicule: v.proprietaireVehicule?.trim() || null,
    immatriculation: v.immatriculation?.trim(),
    marqueVehicule: v.marqueVehicule?.trim() || null,
    telephone: v.assureTelephone?.trim() || null,
    adresse: v.assureAdresse?.trim() || null,
    organismeTrackingId: v.organismeMembreTrackingId || null,
    profession: v.assureProfession || null,
    estPersonneMorale: !!v.estPersonneMorale,
    prochaineVT: v.prochaineVT?.format?.('YYYY-MM-DD') || null,
    capaciteVehicule: v.capaciteVehicule ?? null,
    nbPersonnesABord: v.nbPersonnesABord ?? null,
    aRemorque: !!v.aRemorque,
    remorqueImmatriculation: v.aRemorque ? (v.remorqueImmatriculation?.trim() || null) : null,
    remorqueMarque: v.aRemorque ? (v.remorqueMarque || null) : null,
    remorqueModele: v.aRemorque ? (v.remorqueModele?.trim() || null) : null,
    remorqueAssureur: v.aRemorque ? (v.remorqueAssureur || null) : null,
  };
}

/* Met à jour un assuré existant. Non bloquant : un refus (habilitation
   ASSURE_UPDATE absente, contrainte) ne doit pas empêcher la déclaration, mais
   l'agent doit savoir que sa correction n'a pas été enregistrée. */
async function mettreAJourAssure(assureTrackingId, v) {
  try {
    await axiosInstance.put(`/v1/assures/${assureTrackingId}`, buildAssurePayload(v), { skipErrorToast: true });
  } catch (e) {
    toast.warning(i18n.t('declaration:declarationSinistre.messages.assureNonMisAJour', {
      erreur: e?.response?.data?.message || i18n.t('declaration:declarationSinistre.messages.erreur'),
    }));
  }
}

/* Construit le payload sinistre commun (POST création + PUT édition). */
function buildSinistrePayload(v, organismes) {
  return {
    typeSinistre: v.typeSinistre,
    typeDommage: v.typeDommage,
    dateAccident: v.dateAccident?.format?.('YYYY-MM-DD') || v.dateAccident || null,
    dateDeclaration: v.dateDeclaration?.format?.('YYYY-MM-DD') || v.dateDeclaration || null,
    lieuAccident: v.lieuAccident?.trim(),
    // Ni true ni false quand la question n'a pas ete posee : le backend accepte
    // desormais null, qui se lit « non renseigne » et s'affiche « - ».
    agglomeration: v.agglomeration ?? null,
    positionRc: v.positionRc || null,
    tauxRc: v.tauxRc != null ? Number(v.tauxRc) : null,
    paysGestionnaireTrackingId: v.paysGestionnaireTrackingId || null,
    paysEmetteurTrackingId: v.paysEmetteurTrackingId || null,
    organismeMembreTrackingId: v.organismeMembreTrackingId || null,
    organismeHomologueTrackingId: v.organismeHomologueTrackingId || null,
    numeroSinistreManuel: v.numeroSinistreManuel?.trim() || null,
    numeroSinistreHomologue: v.numeroSinistreHomologue?.trim() || null,
    numeroSinistreEcarteBrune: v.numeroSinistreEcarteBrune?.trim() || null,
    assureurDeclarant: assureurEmetteur(v, organismes),
    numeroSinistreAssureur: v.numeroSinistreAssureur?.trim() || null,
    heureAccident: v.heureAccident?.format?.('HH:mm') || v.heureAccident || null,
    ville: v.ville || null,
    commune: v.commune || null,
    provenance: v.provenance || null,
    destination: v.destination || null,
    circonstances: v.circonstances || null,
    pvEtabli: !!v.pvEtabli,
    attestationIllisible: !!v.attestationIllisible,
    victimesNonIdentifiees: !!v.victimesNonIdentifiees,
    // Conservé comme référence de rapprochement même après passage en « fiches détaillées ».
    nombreVictimesAttendu: v.nombreVictimesAttendu ?? null,
    nombreBlessesAttendu: v.nombreBlessesAttendu ?? null,
    nombreDecedesAttendu: v.nombreDecedesAttendu ?? null,
    motifModificationVictimes: v.motifModificationVictimes?.trim() || null,
    entiteConstatTrackingId: v.entiteConstatTrackingId || null,
    dateEffet: v.dateEffet?.format?.('YYYY-MM-DD') || v.dateEffet || null,
    dateEcheance: v.dateEcheance?.format?.('YYYY-MM-DD') || v.dateEcheance || null,
    assureursSecondairesTrackingIds: v.assureursSecondairesTrackingIds || [],
    conducteurEstAssure: !!v.conducteurEstAssure,
    conducteurNom: v.conducteurEstAssure ? null : (v.conducteurNom || null),
    conducteurPrenom: v.conducteurEstAssure ? null : (v.conducteurPrenom || null),
    conducteurDateNaissance: v.conducteurEstAssure ? null : (v.conducteurDateNaissance?.format?.('YYYY-MM-DD') || v.conducteurDateNaissance || null),
    conducteurNumeroPermis: v.conducteurNumeroPermis || null,
    conducteurCategoriesPermis: v.conducteurCategoriesPermis || [],
    conducteurDateDelivrance: v.conducteurDateDelivrance?.format?.('YYYY-MM-DD') || v.conducteurDateDelivrance || null,
    conducteurLieuDelivrance: v.conducteurLieuDelivrance || null,
    declarantNom: v.declarantNom || null,
    declarantPrenom: v.declarantPrenom || null,
    declarantTelephone: v.declarantTelephone || null,
    declarantQualite: v.declarantQualite || null,
    declarantQualiteDetail: v.declarantQualite === 'AUTRE' ? (v.declarantQualiteDetail || null) : null,
  };
}

/* Reconstitue les valeurs du formulaire depuis un sinistre + son assuré chargés depuis l'API. */
function buildFormValuesFromSinistre(s, a) {
  const toDay = (d) => d ? dayjs(d) : null;
  return {
    // Contexte
    typeSinistre: s.typeSinistre,
    paysEmetteurTrackingId: s.paysEmetteurTrackingId || null,
    paysGestionnaireTrackingId: s.paysGestionnaireTrackingId || null,
    organismeHomologueTrackingId: s.organismeHomologueTrackingId || null,
    numeroSinistreHomologue: s.numeroSinistreHomologue || null,
    numeroSinistreEcarteBrune: s.numeroSinistreEcarteBrune || null,
    numeroSinistreManuel: s.numeroSinistreManuel || null,
    assureurDeclarant: s.assureurDeclarant || null,
    numeroSinistreAssureur: s.numeroSinistreAssureur || null,

    // Contrat & assureurs
    organismeMembreTrackingId: s.organismeMembreTrackingId || null,
    // Repère de assureurEmetteur : l'assureur émetteur enregistré n'est gardé que si
    // l'assureur principal n'a pas été changé.
    organismeMembreInitialTrackingId: s.organismeMembreTrackingId || null,
    // Le backend renvoie une liste d'objets {organismeTrackingId, raisonSociale, code}
    // - on la convertit en liste d'IDs pour le multi-select du wizard.
    assureursSecondairesTrackingIds:
      (s.assureursSecondaires || []).map(a => a.organismeTrackingId).filter(Boolean),
    dateEffet: toDay(s.dateEffet),
    dateEcheance: toDay(s.dateEcheance),
    // La police vit sur l'assuré ; un dossier repris ne la porte que sur le sinistre
    // (`numeroPoliceAssureur`). L'enregistrement la reporte alors sur l'assuré.
    numeroPolice: a?.numeroPolice || s.numeroPoliceAssureur || null,
    numeroAttestation: a?.numeroAttestation || null,
    numeroCGrise: a?.numeroCGrise || null,
    assureTrackingIdExistant: a?.assureTrackingId || null,

    // Assuré & véhicule
    assureNom: a?.nomAssure || null,
    assurePrenom: a?.prenomAssure || null,
    assureTelephone: a?.telephone || null,
    assureAdresse: a?.adresse || null,
    immatriculation: a?.immatriculation || null,
    marqueVehicule: a?.marqueVehicule || null,
    proprietaireVehicule: a?.proprietaireVehicule || null,
    prochaineVT: toDay(a?.prochaineVT),
    capaciteVehicule: a?.capaciteVehicule ?? null,
    nbPersonnesABord: a?.nbPersonnesABord ?? null,
    aRemorque: !!a?.aRemorque,
    remorqueImmatriculation: a?.remorqueImmatriculation || null,
    remorqueMarque: a?.remorqueMarque || null,
    remorqueModele: a?.remorqueModele || null,
    remorqueAssureur: a?.remorqueAssureur || null,
    estPersonneMorale: !!a?.estPersonneMorale,
    assureProfession: a?.profession || null,

    // Conducteur
    conducteurEstAssure: s.conducteurEstAssure ?? true,
    conducteurNom: s.conducteurNom || null,
    conducteurPrenom: s.conducteurPrenom || null,
    conducteurDateNaissance: toDay(s.conducteurDateNaissance),
    conducteurNumeroPermis: s.conducteurNumeroPermis || null,
    conducteurCategoriesPermis: typeof s.conducteurCategoriesPermis === 'string'
      ? s.conducteurCategoriesPermis.split(',').map(c => c.trim()).filter(Boolean)
      : (s.conducteurCategoriesPermis || []),
    conducteurDateDelivrance: toDay(s.conducteurDateDelivrance),
    conducteurLieuDelivrance: s.conducteurLieuDelivrance || null,

    // Accident
    dateAccident: toDay(s.dateAccident),
    dateDeclaration: toDay(s.dateDeclaration) || dayjs(),
    heureAccident: s.heureAccident ? dayjs(s.heureAccident, 'HH:mm') : null,
    lieuAccident: s.lieuAccident || null,
    ville: s.ville || null,
    commune: s.commune || null,
    provenance: s.provenance || null,
    destination: s.destination || null,
    agglomeration: s.agglomeration ?? null,
    typeDommage: s.typeDommage || null,
    circonstances: s.circonstances || null,
    positionRc: s.positionRc || null,
    tauxRc: s.tauxRc != null ? Number(s.tauxRc) : null,
    pvEtabli: !!s.pvEtabli,
    attestationIllisible: !!s.attestationIllisible,
    victimesNonIdentifiees: !!s.victimesNonIdentifiees,
    nombreVictimesAttendu: s.nombreVictimesAttendu ?? null,
    // Un blessé est un blessé : le dossier porte le nombre, pas sa ventilation.
    // Les anciennes colonnes par gravité restent en base mais ne sont ni lues
    // ni réécrites ici - leur contenu était déjà égal à cet agrégat.
    nombreBlessesAttendu: s.nombreBlessesAttendu ?? null,
    nombreDecedesAttendu: s.nombreDecedesAttendu ?? null,
    entiteConstatTrackingId: s.entiteConstatTrackingId || null,

    // Déclarant
    declarantNom: s.declarantNom || null,
    declarantPrenom: s.declarantPrenom || null,
    declarantTelephone: s.declarantTelephone || null,
    declarantQualite: s.declarantQualite || null,
    declarantQualiteDetail: s.declarantQualiteDetail || null,
  };
}

function DraftIndicator({ status }) {
  const { t } = useTranslation('declaration');
  const label =
    status === 'saving' ? t('declarationSinistre.brouillon.enregistrement') :
      status === 'saved' ? t('declarationSinistre.brouillon.enregistre') :
        t('declarationSinistre.brouillon.autoSauvegarde');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.inkMuted }}>
      <CloudOutlined style={{ fontSize: 12, color: status === 'saved' ? T.brand : T.inkLight }} />
      {label}
    </span>
  );
}
